import { FilterState } from './types';

const STORAGE_KEY = 'wemble-search-filter-v1';

interface StoredFilter {
  state: Omit<FilterState, 'searchTerm'>;
  userOfficeIds: string[]; // Detect group/office changes
}

export function getUserOfficeIds(user: any): string[] {
  const officeId = typeof user.office === 'string' ? user.office : user.office?._id;
  const secondaryOfficeId = typeof user.secondaryOffice === 'string' ? user.secondaryOffice : (user.secondaryOffice as any)?._id;
  return [officeId, secondaryOfficeId].filter(Boolean) as string[];
}

export function loadSavedFilter(
  userOfficeIds: string[],
  allOfficeIds?: string[],
): Omit<FilterState, 'searchTerm'> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored: StoredFilter = JSON.parse(raw);

    // Validate structure
    if (!stored.state || !Array.isArray(stored.state.teams) || !Array.isArray(stored.state.advancedFilters)) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    // Reset if user's offices changed (moved to different group)
    const savedIds = [...(stored.userOfficeIds || [])].sort().join(',');
    const currentIds = [...userOfficeIds].sort().join(',');
    if (savedIds !== currentIds) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    // Validate saved team IDs still exist (office may have been deleted)
    if (allOfficeIds && stored.state.teams[0] !== '__all__') {
      const validTeams = stored.state.teams.filter(id => allOfficeIds.includes(id));
      if (validTeams.length === 0) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      stored.state.teams = validTeams;
    }

    return stored.state;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function saveFilter(state: FilterState, userOfficeIds: string[], defaultTeams: string[]) {
  // Don't store if the user is at their default (teams match defaults and no advanced filters)
  const isDefault = arraysEqual(state.teams, defaultTeams)
    && state.advancedFilters.length === 0
    && state.competenceOperator === 'and';

  if (isDefault) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }

  const { searchTerm, ...rest } = state;
  const stored: StoredFilter = { state: rest, userOfficeIds };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
}

export function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((v, i) => v === sortedB[i]);
}
